import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { AlertTriangle, X, Trash2 } from 'lucide-react';
import { MediaPreview } from '../../types/api';
import { useMidias } from '../../hooks/useMidias';
import { Modal, ModalContent, Button } from '../../styles/styled-components';

const DeleteModalContent = styled(ModalContent)`
  padding: ${({ theme }) => theme.spacing[6]};
  max-width: 480px;
  width: 100%;
`;

const DeleteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const DeleteTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const DeleteIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${({ theme }) => theme.colors.error}20;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.error};
`;

const DeleteTitleText = styled.div``;

const DeleteTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const DeleteSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[1]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const DeleteContent = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const WarningBox = styled.div`
  background: ${({ theme }) => theme.colors.warning}10;
  border: 1px solid ${({ theme }) => theme.colors.warning}30;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const WarningText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.warning};
  margin: 0;
`;

const WarningDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const MediaInfo = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const MediaFileName = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
  word-break: break-word;
`;

const MediaMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const MetaItem = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MetaLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const DeleteDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: flex-end;
`;

const ConfirmButton = styled(Button)`
  background: ${({ theme }) => theme.colors.error};
  border-color: ${({ theme }) => theme.colors.error};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    border-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

interface MediaDeleteModalProps {
  media: MediaPreview | null;
  isOpen: boolean;
  userId?: string;
  onClose: () => void;
  onConfirm?: () => void;
}

const MediaDeleteModal: React.FC<MediaDeleteModalProps> = ({
  media,
  isOpen,
  userId,
  onClose,
  onConfirm
}) => {
  const { removeMediaConfig, isRemoving } = useMidias(userId);

  if (!media) return null;

  const handleConfirm = async () => {
    removeMediaConfig(media.fileName, {
      onSuccess: () => {
        onConfirm?.();
        onClose();
      },
    });
  };

  return (
    <Modal visible={isOpen}>
      <DeleteModalContent
        as={motion.div}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
      >
        <DeleteHeader>
          <DeleteTitleContainer>
            <DeleteIcon>
              <AlertTriangle size={24} />
            </DeleteIcon>
            <DeleteTitleText>
              <DeleteTitle>Excluir Mídia</DeleteTitle>
              <DeleteSubtitle>Esta ação não pode ser desfeita</DeleteSubtitle>
            </DeleteTitleText>
          </DeleteTitleContainer>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </DeleteHeader>

        <DeleteContent>
          <WarningBox>
            <WarningHeader>
              <AlertTriangle size={16} />
              <WarningText>Atenção!</WarningText>
            </WarningHeader>
            <WarningDescription>
              Ao excluir esta mídia, sua configuração será removida permanentemente do sistema.
              O arquivo físico permanecerá no servidor, mas não será mais usado pelo bot.
            </WarningDescription>
          </WarningBox>

          <MediaInfo>
            <MediaFileName>{media.fileName}</MediaFileName>
            <MediaMeta>
              <MetaItem>
                <MetaLabel>Tipo:</MetaLabel> {media.tipo === 'video' ? 'Vídeo' : 'Imagem'}
              </MetaItem>
              <MetaItem>
                <MetaLabel>Palavras-chave:</MetaLabel> {media.palavrasChave.length}
              </MetaItem>
              <MetaItem>
                <MetaLabel>Delay:</MetaLabel> {media.delaySegundos}s
              </MetaItem>
            </MediaMeta>
          </MediaInfo>

          <DeleteDescription>
            Tem certeza de que deseja remover a configuração desta mídia?
            Ela não será mais enviada automaticamente pelo bot em resposta às palavras-chave configuradas.
          </DeleteDescription>
        </DeleteContent>

        <ActionButtons>
          <Button variant="ghost" onClick={onClose} disabled={isRemoving}>
            Cancelar
          </Button>
          <ConfirmButton onClick={handleConfirm} disabled={isRemoving}>
            <Trash2 size={16} />
            {isRemoving ? 'Excluindo...' : 'Excluir Mídia'}
          </ConfirmButton>
        </ActionButtons>
      </DeleteModalContent>
    </Modal>
  );
};

export default MediaDeleteModal;